import type { ThemeConfig } from 'antd'
import { lightColors, themeColors, type ThemeMode } from './palettes'

export const tokens = {
  colors: Object.fromEntries(
    Object.entries(lightColors).map(([name, value]) => [name, `var(--apex-color-${name}, ${value})`]),
  ) as typeof lightColors,

  gradients: {
    primary: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
    heading: 'linear-gradient(135deg, var(--apex-color-primary, #4F46E5), var(--apex-color-secondary, #7C3AED))',
    primarySubtle: 'linear-gradient(135deg, #4F46E512, #7C3AED12)',
    sidebar: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
    login: 'linear-gradient(160deg, #1E1B4B, #312E81, #4F46E5)',
    accent: 'linear-gradient(180deg, #4F46E5, #7C3AED)',
    surface: 'linear-gradient(135deg, var(--apex-color-background, #F8FAFC), color-mix(in srgb, var(--apex-color-primary, #4F46E5) 8%, var(--apex-color-surface, #FFFFFF)))',
  },

  shadows: {
    card: '0 1px 3px rgba(79,70,229,0.04), 0 1px 2px rgba(0,0,0,0.02)',
    cardHover: '0 8px 28px rgba(79,70,229,0.12), 0 4px 12px rgba(0,0,0,0.04)',
    button: '0 4px 14px rgba(79,70,229,0.25)',
    buttonHover: '0 8px 22px rgba(79,70,229,0.35)',
    sidebar: '2px 0 12px rgba(79,70,229,0.04)',
    header: '0 1px 4px rgba(79,70,229,0.03)',
    modal: '0 20px 60px rgba(79,70,229,0.15), 0 8px 24px rgba(0,0,0,0.06)',
    input: '0 0 0 2px rgba(79,70,229,0.12)',
  },

  radius: {
    sm: 6,
    md: 8,
    lg: 12,
    xl: 14,
    xxl: 16,
    pill: 999,
  },

  font: {
    family: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif',
  },

  spacing: {
    page: 28,
    card: 24,
    section: 20,
  },

  transition: {
    default: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    fast: 'all 0.15s ease',
  },
} as const

export function createAntTheme(mode: ThemeMode): ThemeConfig {
  // Ant Design 用具体颜色生成派生色；普通页面用上方 CSS 变量即时响应主题。
  const colors = themeColors[mode]
  return {
    token: {
      colorPrimary: colors.primarySolid,
      colorLink: colors.primary,
      colorLinkHover: colors.primaryLight,
      borderRadius: tokens.radius.md,
      colorBgContainer: colors.surface,
      colorBgElevated: colors.surface,
      colorBgLayout: colors.background,
      colorBorder: colors.border,
      colorBorderSecondary: colors.borderLight,
      colorText: colors.text,
      colorTextSecondary: colors.textSecondary,
      ...(mode === 'dark' ? {
        colorTextTertiary: colors.textMuted,
        colorTextPlaceholder: colors.textPlaceholder,
        colorPrimaryText: colors.primary,
        colorPrimaryTextHover: colors.primaryLight,
        colorPrimaryTextActive: colors.primary,
      } : {}),
      fontFamily: tokens.font.family,
    },
    components: {
      ...(mode === 'dark' ? {
        Tabs: {
          itemSelectedColor: colors.primary,
          itemHoverColor: colors.primaryLight,
          itemActiveColor: colors.primary,
          inkBarColor: colors.primary,
        },
        Menu: {
          itemSelectedColor: colors.primary,
          subMenuItemSelectedColor: colors.primary,
        },
      } : {}),
      Table: {
        headerBg: colors.background,
        headerColor: colors.textSecondary,
        rowHoverBg: colors.surfaceHover,
        borderColor: colors.borderLight,
      },
      Card: {
        borderRadiusLG: tokens.radius.xl,
      },
      Modal: {
        borderRadiusLG: tokens.radius.xl,
      },
      Button: {
        borderRadius: tokens.radius.md,
        controlHeight: 36,
      },
      Input: {
        borderRadius: tokens.radius.md,
      },
      Select: {
        borderRadius: tokens.radius.md,
      },
    },
  }
}

export const antThemeTokens = createAntTheme('light')

export const styles = {
  card: {
    background: tokens.colors.surface,
    borderRadius: tokens.radius.xl,
    border: `1px solid ${tokens.colors.border}`,
    boxShadow: tokens.shadows.card,
    transition: tokens.transition.default,
  } as React.CSSProperties,

  cardHover: {
    boxShadow: tokens.shadows.cardHover,
    transform: 'translateY(-2px)',
  } as React.CSSProperties,

  gradientText: {
    background: tokens.gradients.heading,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  } as React.CSSProperties,

  primaryButton: {
    background: tokens.gradients.primary,
    border: 'none',
    boxShadow: tokens.shadows.button,
  } as React.CSSProperties,
}
